import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Menu, X, Phone, Mail, MapPin, Clock, ArrowRight, 
  Shield, Scale, FileText, Building2, Users, TrendingUp,
  Calculator, Briefcase,
  Facebook, Twitter, Linkedin, Youtube, Send, ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Toaster, toast } from "sonner";
import OurPeoplePage from "@/pages/OurPeople";
import JoinUsPage from "@/pages/JoinUs";
import AdminPage from "@/pages/Admin";
import { submitContactForm, subscribeNewsletter } from "@/lib/api";

// Firm Data
const firmData = {
  name: "Sanjiv Shah & Associates LLP",
  shortName: "SSA",
  tagline: "Excellence in Audit, Taxation & Advisory since 1984",
  established: "1984",
  experience: "40+",
  email: "contact@ssaca.in",
  phone: "+91 44 2561 0842",
  description: "Sanjiv Shah and Associates LLP(SSA) is a Chartered Accountancy firm in India with offices in Chennai, Coimbatore and Hyderabad. Established in 1984 under the leadership of CA Sanjiv Kumar Shah, The firm is built on a strong foundation of expertise, integrity and professional commitment. Its team comprises experienced partners supported by a dedicated group of dynamic professionals.",
  vision: "SSA is driven by a clear vision to build a reputable and focused practice in its areas of specialization. The firm is committed to fostering growth for its people while consistently delivering value-driven services that align with the best interests of its clients."
};

const services = [
  { icon: Shield, title: "Audit & Assurance", desc: "Robust statutory and internal audit services designed to ensure regulatory compliance, strengthen controls and uphold the accuracy and integrity of financial reporting." },
  { icon: Calculator, title: "Direct Taxation", desc: "Delivering strategic tax planning, end-to-end compliance and representation services tailored to meet the needs of both individuals and businesses." },
  { icon: Scale, title: "GST & Indirect Taxation", desc: "Comprehensive GST advisory, compliance management and litigation support services delivered with precision and regulatory expertise." },
  { icon: Building2, title: "Business Advisory", desc: "Providing strategic advisory services to support business growth, organizational restructuring and the execution of corporate transactions with clarity and precision." },
  { icon: FileText, title: "Corporate Law", desc: "Expert assistance in company law matters, secretarial services and corporate governance, ensuring robust compliance frameworks and sound corporate practices." },
  { icon: Briefcase, title: "Business Support", desc: "End-to-end accounting, bookkeeping and back-office support services designed to enhance accuracy, streamline operations and support efficient financial management." },
  { icon: TrendingUp, title: "Risk Advisory", desc: "Independent evaluation of internal controls, structured risk management and targeted process improvements to reinforce governance, reduce exposure and enhance operational performance." },
  { icon: Users, title: "Artificial Intelligence", desc: "Tailored AI Services to solve real business challenges by delivering results driven AI solutions." }
];

const sectors = [
  "Agriculture", "Banking & Finance", "Construction & Real Estate", "Chemicals & Metals",
  "E-Commerce", "Education", "FMCG", "Food & Hospitality", "Government & Public Utilities",
  "Infrastructure", "Insurance", "Information Technology", "Logistics & Warehousing",
  "Media & Entertainment", "Manufacturing & Trading", "Oil & Mining", "Pharma & Healthcare",
  "Social Welfare", "Stock Broking", "Telecommunication", "Transport"
];

const offices = [
  {
    city: "Chennai",
    address: "#59, VII Floor, Empee Tower, Adithanar Salai, Chennai – 600 002",
    phone: "+91 44 2859 0892 "
  },
  {
    city: "Coimbatore",
    address: "#203/2, I Floor, 9th Street Cross Cut Road, Gandhipuram, Coimbatore – 641 012",
    phone: "+91 42 2357 8202"
  },
  {
    city: "Hyderabad",
    address: "#8-2-684/J/8, I Floor, Bhavani Nagar, Road No.12, Banjara Hills, Hyderabad – 500 034",
    phone: "+91 63 0115 4946"
  }
];

const stats = [
  { number: "40+", label: "Years of Excellence" },
  { number: "500+", label: "Valued Clients" },
  { number: "21", label: "Industry Sectors" },
  { number: "3", label: "Office Locations" }
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Components
const Navbar = ({ isHomePage = true }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "About", href: isHomePage ? "#about" : "/#about" },
    { label: "Services", href: isHomePage ? "#services" : "/#services" },
    { label: "Sectors", href: isHomePage ? "#sectors" : "/#sectors" },
    { label: "Contact", href: isHomePage ? "#contact" : "/#contact" }
  ];

  const shouldInvert = isHomePage && !isScrolled;

  return (
    <>
      <nav 
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !isHomePage ? "glass shadow-sm border-b border-slate-200" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" data-testid="logo" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_audit-experts-6/artifacts/e6e7ccut_3467b892-0442-4ffa-8cf9-3f36ffd8d74b-removebg-preview.png" 
                alt="Sanjiv Shah & Associates - Chartered Accountants"
                className={`h-12 sm:h-14 w-auto object-contain transition-all ${
                  shouldInvert ? "brightness-0 invert" : ""
                }`}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={`nav-link text-xs font-semibold uppercase tracking-widest transition-colors ${
                    shouldInvert ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-[#0B1120]"
                  }`}
                >
                  {link.label}
                </a>
              ))}
              
              {/* People Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className={`nav-link text-xs font-semibold uppercase tracking-widest transition-colors flex items-center gap-1 ${
                  shouldInvert ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-[#0B1120]"
                }`}>
                  People
                  <ChevronDown size={14} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/our-people" className="cursor-pointer">
                      <Users size={16} className="mr-2" />
                      Our People
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/careers" className="cursor-pointer">
                      <Briefcase size={16} className="mr-2" />
                      Join Us
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <a href={isHomePage ? "#contact" : "/#contact"}>
                <Button 
                  data-testid="nav-cta-btn"
                  className={`rounded-md px-6 transition-all ${
                    shouldInvert 
                      ? "bg-[#C5A059] hover:bg-white hover:text-[#0B1120] text-white" 
                      : "bg-[#0B1120] hover:bg-[#C5A059] text-white"
                  }`}
                >
                  Get in Touch
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden p-2 ${shouldInvert ? "text-white" : "text-[#0B1120]"}`}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div 
        data-testid="mobile-menu"
        className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_audit-experts-6/artifacts/e6e7ccut_3467b892-0442-4ffa-8cf9-3f36ffd8d74b-removebg-preview.png" 
              alt="SSA Logo"
              className="h-10 w-auto object-contain brightness-0 invert"
            />
            <button onClick={() => setMobileMenuOpen(false)} className="text-white">
              <X size={24} />
            </button>
          </div>
          <div className="flex flex-col gap-6">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-white/80 hover:text-[#C5A059] text-lg font-medium transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="border-t border-white/20 pt-4 mt-2">
              <p className="text-white/50 text-sm uppercase tracking-widest mb-4">People</p>
              <Link
                to="/our-people"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 text-white/80 hover:text-[#C5A059] text-lg font-medium transition-colors mb-3"
              >
                <Users size={18} />
                Our People
              </Link>
              <Link
                to="/careers"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 text-white/80 hover:text-[#C5A059] text-lg font-medium transition-colors"
              >
                <Briefcase size={18} />
                Join Us
              </Link>
            </div>
            <a href={isHomePage ? "#contact" : "/#contact"} onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full bg-[#C5A059] hover:bg-white hover:text-[#0B1120] mt-4">
                Get in Touch
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

const HeroSection = () => (
  <section 
    data-testid="hero-section"
    className="hero-section relative"
    style={{
      backgroundImage: "url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80')"
    }}
  >
    <div className="hero-overlay absolute inset-0" />
    <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-56 md:py-32">
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="max-w-3xl"
      >
        <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-6">
          <div className="section-divider" />
          <span className="text-[#C5A059] font-medium text-sm uppercase tracking-widest">
            Established {firmData.established}
          </span>
        </motion.div>
        
        <motion.h1 
          variants={fadeInUp}
          className="logo-font text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6"
        >
          <span className="text-[#4A7C9B]">SANJIV SHAH</span>
          <br />
          <span className="text-white/90 text-3xl sm:text-4xl lg:text-5xl">& Associates </span>
          <span className="text-[#C5A059] text-3xl sm:text-4xl lg:text-5xl">LLP</span>
        </motion.h1>
        
        <motion.p 
          variants={fadeInUp}
          className="text-xl sm:text-2xl text-white/90 font-light mb-8"
        >
          {firmData.tagline}
        </motion.p>
        
        <motion.p 
          variants={fadeInUp}
          className="text-white/70 text-base sm:text-lg leading-relaxed mb-10 max-w-2xl"
        >
          A powerful blend of seasoned partners and a passionate team of dynamic professionals, we bring together deep industry insight and forward-thinking energy to deliver exceptional results. Our collective expertise enables us to navigate complex challenges with precision, while our commitment to excellence ensures consistent, high-quality outcomes.
          <br></br>
          <br></br>
          We proudly serve a diverse clientele—including Government Departments, Public Sector Undertakings (PSUs), Multinational Corporates and MSMEs—across India. With a client-centric approach, we focus on building long-term partnerships, offering tailored solutions and driving sustainable growth through integrity, innovation and professionalism.
        </motion.p>
        
        <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
          <a href="#contact">
            <Button 
              data-testid="hero-contact-btn"
              size="lg" 
              className="bg-[#C5A059] hover:bg-white hover:text-[#0B1120] text-white px-8 py-6 text-base rounded-md transition-all"
            >
              Schedule Consultation
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </a>
          <a href="#services">
            <Button 
              data-testid="hero-services-btn"
              size="lg" 
              variant="outline" 
              className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base rounded-md"
            >
              Our Services
            </Button>
          </a>
        </motion.div>
      </motion.div>
    </div>

    {/* Stats Bar */}
    <div className="absolute bottom-0 left-0 right-0 bg-white/10 backdrop-blur-md border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div 
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 + index * 0.1 }}
              className="text-center"
            >
              <div className="text-3xl sm:text-4xl font-serif font-bold text-[#C5A059] mb-1">
                {stat.number}
              </div>
              <div className="text-white/70 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const AboutSection = () => (
  <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="section-divider" />
            <span className="text-[#C5A059] font-medium text-sm uppercase tracking-widest">
              About Us
            </span>
          </div>
          
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1120] font-bold mb-6">
            Four Decades of <span className="text-[#C5A059]">Trust</span> & Excellence
          </h2>
          
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-6">
            {firmData.description}
          </p>
          
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-8">
            {firmData.vision}
          </p>
          
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80" 
              alt="Modern Office" 
              className="w-full h-[400px] lg:h-[500px] object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0B1120] to-transparent p-8">
              <div className="text-white">
                <div className="text-5xl font-serif font-bold text-[#C5A059] mb-2">
                  {firmData.experience}
                </div>
                <div className="text-white/80">Years of Professional Excellence</div>
              </div>
            </div>
          </div>
          
          {/* Floating card */}
          <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs hidden lg:block">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
                <Shield className="text-[#C5A059]" size={24} />
              </div>
              <div>
                <div className="font-semibold text-[#0B1120]">Trusted Partner</div>
                <div className="text-sm text-slate-500">Government & PSUs</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const ServicesSection = () => (
  <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-slate-50 noise-overlay">
    <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="section-divider" />
          <span className="text-[#C5A059] font-medium text-sm uppercase tracking-widest">
            Our Expertise
          </span>
          <div className="section-divider" />
        </div>
        
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1120] font-bold mb-4">
          Comprehensive Professional Services
        </h2>
        <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto">
          Providing high-quality audit, taxation and advisory solutions, driven by a steadfast commitment to precision, integrity and professional excellence.
        </p>
      </motion.div>
      
      <motion.div 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {services.map((service, index) => (
          <motion.div
            key={service.title}
            variants={fadeInUp}
            className="service-card card-hover bg-white rounded-xl p-8"
          >
            <div className="service-icon mb-6">
              <service.icon size={24} />
            </div>
            <h3 className="font-serif text-xl font-semibold text-[#0B1120] mb-3">
              {service.title}
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              {service.desc}
            </p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

const SectorsSection = () => (
  <section id="sectors" data-testid="sectors-section" className="py-24 md:py-32 bg-[#0B1120]">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="section-divider" />
          <span className="text-[#C5A059] font-medium text-sm uppercase tracking-widest">
            Industry Focus
          </span>
          <div className="section-divider" />
        </div>
        
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white font-bold mb-4">
          Serving Diverse Sectors
        </h2>
        <p className="text-slate-400 text-base sm:text-lg max-w-2xl mx-auto">
          A broad client base spanning Government Departments, Public Sector Undertakings, Local Authorities, multinational corporations, listed entities and MSMEs, reflecting extensive cross-sector experience and capability.
        </p>
      </motion.div>
      
      <motion.div 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="flex flex-wrap justify-center gap-3"
      >
        {sectors.map((sector, index) => (
          <motion.span
            key={sector}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.03 }}
            className="sector-tag cursor-default"
          >
            {sector}
          </motion.span>
        ))}
      </motion.div>
    </div>
  </section>
);

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }

    if (formData.message.length < 10) {
      toast.error("Please provide a longer message (at least 10 characters).");
      return;
    }

    setLoading(true);
    
    try {
      await submitContactForm(formData);
      toast.success("Thank you! We'll get back to you shortly.");
      setFormData({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterLoading(true);
    
    try {
      await subscribeNewsletter({ email: newsletterEmail });
      toast.success("Successfully subscribed to our newsletter!");
      setNewsletterEmail("");
    } catch (error) {
      if (error.message && error.message.includes("already")) {
        toast.error("This email is already subscribed.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="section-divider" />
              <span className="text-[#C5A059] font-medium text-sm uppercase tracking-widest">
                Get in Touch
              </span>
            </div>
            
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1120] font-bold mb-4">
              Schedule a Consultation
            </h2>
            <p className="text-slate-600 text-base sm:text-lg mb-8">
              Let us help you navigate the complexities of taxation, audit, and business advisory.
            </p>
            
            <form onSubmit={handleSubmit} className="contact-form space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <Input
                  data-testid="contact-name-input"
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-14"
                />
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-14"
                />
              </div>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <Input
                  data-testid="contact-phone-input"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-14"
                />
                <Input
                  data-testid="contact-company-input"
                  placeholder="Company Name"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="h-14"
                />
              </div>
              
              <Select 
                value={formData.service} 
                onValueChange={(value) => setFormData({ ...formData, service: value })}
              >
                <SelectTrigger data-testid="contact-service-select" className="h-14">
                  <SelectValue placeholder="Type of Service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.title} value={service.title}>
                      {service.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Textarea
                data-testid="contact-message-input"
                placeholder="Tell us about your requirements *"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={5}
                className="resize-none"
              />
              
              <Button 
                data-testid="contact-submit-btn"
                type="submit" 
                disabled={loading}
                className="w-full sm:w-auto bg-[#0B1120] hover:bg-[#C5A059] text-white px-8 py-6 text-base rounded-md transition-all"
              >
                {loading ? (
                  <>
                    <span className="spinner mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send size={18} className="ml-2" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
          
          {/* Office Locations */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="font-serif text-2xl font-semibold text-[#0B1120] mb-8">
              Our Offices
            </h3>
            
            <div className="space-y-6">
              {offices.map((office) => (
                <div key={office.city} className="office-card">
                  <h4 className="font-serif text-xl font-semibold text-[#0B1120] mb-4">
                    {office.city}
                  </h4>
                  <div className="space-y-3 text-slate-600">
                    <div className="flex items-start gap-3">
                      <MapPin size={18} className="text-[#C5A059] mt-0.5 shrink-0" />
                      <span className="text-sm">{office.address}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone size={18} className="text-[#C5A059] shrink-0" />
                      <span className="text-sm">{office.phone}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Newsletter */}
            <div className="mt-8 p-8 bg-[#0B1120] rounded-xl">
              <h4 className="font-serif text-xl font-semibold text-white mb-3">
                Stay Updated
              </h4>
              <p className="text-slate-400 text-sm mb-6">
                Subscribe to our newsletter for the latest updates on tax laws and regulations.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-3">
                <Input
                  data-testid="newsletter-email-input"
                  type="email"
                  placeholder="Enter your email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  required
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                />
                <Button 
                  data-testid="newsletter-submit-btn"
                  type="submit"
                  disabled={newsletterLoading}
                  className="bg-[#C5A059] hover:bg-white hover:text-[#0B1120] px-6"
                >
                  {newsletterLoading ? <span className="spinner" /> : <Send size={18} />}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer data-testid="footer" className="bg-[#0B1120] pt-20 pb-8">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
        {/* Brand */}
        <div>
          <div className="mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_audit-experts-6/artifacts/e6e7ccut_3467b892-0442-4ffa-8cf9-3f36ffd8d74b-removebg-preview.png" 
              alt="Sanjiv Shah & Associates - Chartered Accountants"
              className="h-16 w-auto object-contain brightness-0 invert"
            />
          </div>
          <p className="text-slate-400 text-sm leading-relaxed mb-6">
            A Chartered Accountancy firm serving clients across India since 1984.
          </p>
          <div className="flex gap-4">
            <a href="https://www.facebook.com/firmssa/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-slate-400 hover:bg-[#C5A059] hover:text-white transition-all">
              <Facebook size={18} />
            </a>
            <a href="https://twitter.com/firmssa?s=09" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-slate-400 hover:bg-[#C5A059] hover:text-white transition-all">
              <Twitter size={18} />
            </a>
            <a href="https://www.linkedin.com/company/firmssa" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-slate-400 hover:bg-[#C5A059] hover:text-white transition-all">
              <Linkedin size={18} />
            </a>
            <a href="https://www.youtube.com/channel/UCJ2tW-xSR2rOxr6cjwarFKw?view_as=subscriber" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-slate-400 hover:bg-[#C5A059] hover:text-white transition-all">
              <Youtube size={18} />
            </a>
          </div>
        </div>
        
        {/* Quick Links */}
        <div>
          <h4 className="font-serif text-lg font-semibold text-white mb-6">Quick Links</h4>
          <ul className="space-y-3">
            {["About Us", "Services", "Sectors", "Contact"].map((link) => (
              <li key={link}>
                <a href={`/#${link.toLowerCase().replace(" ", "")}`} className="footer-link text-sm">
                  {link}
                </a>
              </li>
            ))}
          </ul>
        </div>
        
        {/* People & Careers */}
        <div>
          <h4 className="font-serif text-lg font-semibold text-white mb-6">People & Careers</h4>
          <ul className="space-y-3">
            <li>
              <Link to="/our-people" className="footer-link text-sm">
                Our People
              </Link>
            </li>
            <li>
              <Link to="/our-people" className="footer-link text-sm">
                SSA Alumni
              </Link>
            </li>
            <li>
              <Link to="/careers" className="footer-link text-sm">
                Join Us
              </Link>
            </li>
            <li>
              <Link to="/careers" className="footer-link text-sm">
                Current Openings
              </Link>
            </li>
          </ul>
        </div>
        
      </div>
      
      {/* Bottom */}
      <div className="border-t border-white/10 pt-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Sanjiv Shah & Associates LLP. All rights reserved.
          </p>
          <div className="flex gap-6 items-center">
            <a href="#" className="text-slate-500 hover:text-[#C5A059] text-sm transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-slate-500 hover:text-[#C5A059] text-sm transition-colors">
              Terms of Service
            </a>
            <a
              href="https://www.insiconnect.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-[#C5A059] text-sm transition-colors"
            >
              Made by Insiconnect
            </a>
          </div>
        </div>
      </div>
    </div>
  </footer>
);

// Home Page Component
const HomePage = () => (
  <>
    <Navbar isHomePage={true} />
    <main>
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <SectorsSection />
      <ContactSection />
    </main>
    <Footer />
  </>
);

// Layout for other pages
const PageLayout = ({ children }) => (
  <>
    <Navbar isHomePage={false} />
    {children}
    <Footer />
  </>
);

function App() {
  return (
    <BrowserRouter>
      <div className="App">
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/our-people" element={
            <PageLayout>
              <OurPeoplePage />
            </PageLayout>
          } />
          <Route path="/careers" element={
            <PageLayout>
              <JoinUsPage />
            </PageLayout>
          } />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
